import * as React from 'react';
import { DigitalMapPin } from 'models/digitalMap';
import { useMapDisplayContext } from '../MapDisplayContext';
import { PinDisplay } from '../PinDisplay';
import { fromLonLat } from 'ol/proj';
import { Feature, Map, Overlay } from 'ol';
import { Point } from 'ol/geom';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Icon } from 'ol/style';
import { createPortal } from 'react-dom';

// Create a transparent 1x1 pixel image
const transparentPixel =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface Props {
  map: Map;
  pin: DigitalMapPin;
  isActive: boolean;
  onClick?: (pinKey: string) => void;
}

export const OpenLayersPinMarker: React.FC<Props> = ({
  map,
  pin,
  isActive,
  onClick,
}): React.JSX.Element | null => {
  const { activePage } = useMapDisplayContext();
  const [markerElement, setMarkerElement] = React.useState<HTMLDivElement | null>(null);
  const featureRef = React.useRef<Feature<Point> | null>(null);
  const layerRef = React.useRef<VectorLayer<VectorSource> | null>(null);
  const overlayRef = React.useRef<Overlay | null>(null);

  React.useEffect(() => {
    // Create a container for the React component
    const container = document.createElement('div');
    setMarkerElement(container);

    // Create the feature with coordinates
    const coordinates = fromLonLat([pin.location?.longitude ?? 0, pin.location?.latitude ?? 0]);
    const feature = new Feature({
      geometry: new Point(coordinates),
    });

    // Create a style for the feature
    const style = new Style({
      image: new Icon({
        src: transparentPixel,
        anchor: [0.5, 1],
        scale: 1,
        size: [1, 1], // Set explicit size
      }),
      zIndex: isActive ? 2 : 1,
    });

    feature.setStyle(style);
    featureRef.current = feature;

    // Create a vector layer for the feature
    const source = new VectorSource({
      features: [feature],
    });

    const layer = new VectorLayer({
      source: source,
    });

    layerRef.current = layer;

    // Create an overlay for the React component
    const overlay = new Overlay({
      element: container,
      position: coordinates,
      positioning: 'bottom-center',
      offset: [0, 0],
    });

    overlayRef.current = overlay;
    map.addOverlay(overlay);

    // Add the layer to the map
    map.addLayer(layer);

    return () => {
      map.removeLayer(layer);
      map.removeOverlay(overlay);
    };
  }, [map, pin, isActive, onClick, activePage]);

  const coordinates = fromLonLat([pin.location?.longitude ?? 0, pin.location?.latitude ?? 0]);

  // Add click handler
  const clickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClick?.(pin.key);
    if (!activePage.startsWith('ITINERARIES')) {
      const view = map.getView();
      view.animate({
        center: coordinates,
        duration: 500,
      });
    }
  };

  return markerElement
    ? createPortal(
        <div onClick={clickHandler}>
          <PinDisplay pin={pin} isActive={isActive} />
        </div>,
        markerElement
      )
    : null;
};
